import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

export default function PdfExportMenu({
  options = [],
  values = [],
  onChange,
  onExport,
  disabled,
  generating,
  title = "Choose reports to export to PDF",
}) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const selected = new Set(values || []);

  useEffect(() => {
    const close = (event) => {
      if (
        !rootRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return undefined;
    }

    const positionMenu = () => {
      const buttonRect = buttonRef.current?.getBoundingClientRect();
      if (!buttonRect) return;

      const gutter = 8;
      const gap = 4;
      const menuWidth = menuRef.current?.offsetWidth || 224;
      const menuHeight = menuRef.current?.offsetHeight || 164;
      const fitsToRight = buttonRect.left + menuWidth <= window.innerWidth - gutter;
      const left = fitsToRight
        ? buttonRect.left
        : Math.max(gutter, buttonRect.right - menuWidth);
      const fitsBelow = buttonRect.bottom + gap + menuHeight <= window.innerHeight - gutter;
      const top = fitsBelow
        ? buttonRect.bottom + gap
        : Math.max(gutter, buttonRect.top - gap - menuHeight);

      setMenuPosition({ left, top });
    };

    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open]);

  const toggle = (value) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange?.(options.map((option) => option.value).filter((key) => next.has(key)));
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-primary text-sm"
        onClick={() => setOpen((value) => !value)}
        disabled={disabled || generating}
        aria-haspopup="menu"
        aria-expanded={open}
        title={title}
      >
        {generating ? "Generating PDF..." : "Export PDF"}
      </button>

      {open && !disabled && !generating
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              className="fixed z-[60] w-56 rounded-md border border-border bg-app py-1 shadow-lg"
              style={menuPosition || { left: 0, top: 0, visibility: "hidden" }}
            >
              {options.map((option) => {
                const checked = selected.has(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={checked}
                    onClick={() => toggle(option.value)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-app hover:bg-[hsl(var(--muted))/0.25]"
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-input">
                      {checked ? <Check className="h-3 w-3" /> : null}
                    </span>
                    <span>{option.label}</span>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={async () => {
                  setOpen(false);
                  await onExport?.();
                }}
                disabled={!selected.size}
                className="mt-1 w-full border-t border-border px-3 py-2 text-left text-sm font-medium text-app transition-colors hover:bg-[hsl(var(--brand1)/0.12)] hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-app"
              >
                Export Selected
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
